<template>
  <div class="tn-booking">
    <template v-if="loading">
      <v-skeleton-loader type="card-heading, list-item-two-line" class="mb-6"></v-skeleton-loader>
      <div class="tn-booking__grid">
        <v-skeleton-loader type="card, card"></v-skeleton-loader>
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </div>
    </template>

    <v-card v-else-if="!booking" class="tn-empty py-12">
      <v-icon size="44">mdi-file-alert-outline</v-icon>
      <div>Couldn't get the booking details.</div>
      <v-btn text color="primary" :to="{ name: 'tripBookings' }">Back to package bookings</v-btn>
    </v-card>

    <template v-else>
      <v-alert v-if="!booking.package_details" type="info" text dense class="mb-5">
        The booked itinerary, the customer's account and the payment show up once the latest API update is deployed.
      </v-alert>

      <booking-summary
        :booking="booking"
        type="packages"
        icon="mdi-beach"
        :title="details.package.title || 'Package booking'"
        :tags="tags"
        :total="booking.total_price"
        :total-label="details.inquiry ? 'Quoted total' : 'Booking total'"
      />

      <div class="tn-booking__grid">
        <div class="tn-booking__col">
          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-calendar-range</v-icon> Trip</div>
            <div class="tn-panel__body">
              <stay-strip
                start-label="Starts"
                end-label="Ends"
                :start="details.stay.start"
                :end="details.stay.end"
                :middle="details.stay.days ? details.stay.days + ' day' + (details.stay.days === 1 ? '' : 's') : ''"
              />
              <dl class="tn-facts">
                <div><dt>Package</dt><dd>{{ details.package.title || '—' }}<span v-if="details.package.deleted" class="tn-tag">Removed from the catalog</span></dd></div>
                <div><dt>Guests</dt><dd>{{ guestsLabel }}</dd></div>
                <div><dt>Price per adult</dt><dd>{{ Number(booking.trip_price_per_person) > 0 ? money(booking.trip_price_per_person) : '—' }}</dd></div>
                <div><dt>Booking type</dt><dd>{{ details.inquiry ? 'Inquiry: trip with a Nile cruise, no online payment' : 'Online booking' }}</dd></div>
              </dl>
            </div>
          </v-card>

          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-map-marker-path</v-icon> Itinerary</div>
            <div class="tn-panel__body">
              <ul v-if="details.itinerary.length" class="tn-itinerary">
                <li v-for="(segment, i) in details.itinerary" :key="i" class="tn-segment">
                  <span class="tn-segment__marker" :class="{ 'is-cruise': segment.type === 'cruise' }">
                    <v-icon size="12">{{ segment.type === 'cruise' ? 'mdi-ferry' : 'mdi-map-marker' }}</v-icon>
                  </span>
                  <div class="tn-segment__head">
                    <span class="tn-segment__title">{{ segmentTitle(segment) }}</span>
                    <span class="tn-muted">{{ dayRange(segment.first_day, segment.last_day) }}<template v-if="segment.first_date"> · {{ dateRange(segment.first_date, segment.last_date) }}</template></span>
                  </div>

                  <div v-if="segment.type === 'cruise'" class="tn-day">
                    <div class="tn-day__when">{{ dayRange(segment.first_day, segment.last_day) }}<span>{{ segment.days.length }} days on board</span></div>
                    <div class="tn-day__what">
                      {{ (segment.cruise && segment.cruise.name) || 'Nile cruise' }}
                      <span v-if="segment.cruise && segment.cruise.deleted" class="tn-tag">Removed from the catalog</span>
                      <div v-if="segment.city" class="tn-muted">Boarding in {{ segment.city }}</div>
                    </div>
                  </div>

                  <template v-else>
                    <div v-for="day in segment.days" :key="day.day" class="tn-day">
                      <div class="tn-day__when">Day {{ day.day }}<span v-if="day.date">{{ shortDate(day.date) }}</span></div>
                      <div class="tn-day__what">
                        <span v-if="day.free" class="tn-muted">Free day</span>
                        <div v-for="activity in day.activities" :key="activity.id">
                          {{ activity.title || 'Activity #' + activity.id }}
                          <span v-if="activity.duration" class="tn-muted">· {{ activity.duration }}</span>
                          <span v-if="activity.deleted" class="tn-tag">Removed from the catalog</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </li>
              </ul>
              <p v-else class="tn-muted mb-0">{{ booking.package_details ? 'No itinerary was saved with this booking.' : 'Available after the API update.' }}</p>

              <div v-if="details.unsaved_days" class="tn-note">
                <v-icon size="18">mdi-information-outline</v-icon>
                <span>{{ dayRange(details.unsaved_days.from, details.unsaved_days.to) }} of the trip have no activities saved with this booking.</span>
              </div>
            </div>
          </v-card>

          <v-card v-if="details.hotels.length" class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-bed-outline</v-icon> Hotels</div>
            <v-simple-table>
              <thead>
                <tr><th>Hotel</th><th>Check-in</th><th>Check-out</th><th>Code</th><th v-if="hasTgxHotels">Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="(hotel, i) in details.hotels" :key="i">
                  <td>
                    <div class="font-weight-medium">{{ hotel.name || 'Hotel no longer in the catalog' }}</div>
                    <div v-if="hotel.address" class="tn-muted">{{ hotel.address }}</div>
                    <div v-if="hotel.source === 'travelgatex'" class="tn-muted">
                      {{ [hotel.city, hotel.room, hotel.board].filter(Boolean).join(' · ') }}
                      <span v-if="hotel.guests"> · {{ hotel.guests.rooms }} room(s), {{ hotel.guests.adults }} adult(s){{ hotel.guests.children ? ', ' + hotel.guests.children + ' child(ren)' : '' }}</span>
                      <span v-if="hotel.total"> · {{ hotel.currency }} {{ hotel.total }}</span>
                    </div>
                  </td>
                  <td class="text-no-wrap">{{ date(hotel.start) }}</td>
                  <td class="text-no-wrap">{{ date(hotel.end) }}</td>
                  <td>
                    {{ hotel.code || '—' }}
                    <div v-if="hotel.reference" class="tn-muted">Hotel ref {{ hotel.reference }}</div>
                    <div v-if="hotel.client_reference" class="tn-muted">{{ hotel.client_reference }}</div>
                  </td>
                  <td v-if="hasTgxHotels">
                    <v-chip v-if="hotel.status" small label :color="tgxStatusColor(hotel.status)" text-color="white">{{ hotel.status_label }}</v-chip>
                    <div v-if="hotel.refund_amount" class="tn-muted">Refund {{ hotel.currency }} {{ hotel.refund_amount }}</div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <travellers-panel :travellers="travellers" />
        </div>

        <div class="tn-booking__col">
          <customer-panel
            :customer="customer"
            :contact="contact"
            :empty-text="hasOverview ? 'No customer account is linked to this booking (guest checkout).' : 'Available after the API update.'"
          >
            <div v-if="hasPickup" class="tn-subsection">
              <div class="tn-subsection__title">Pickup</div>
              <div class="tn-contact mt-1">
                <span v-if="contact.pickup_hotel_name"><v-icon size="18">mdi-office-building-marker-outline</v-icon>{{ contact.pickup_hotel_name }}</span>
                <span v-if="contact.pickup_room_number"><v-icon size="18">mdi-door</v-icon>Room {{ contact.pickup_room_number }}</span>
              </div>
            </div>
          </customer-panel>
          <payment-panel
            :payment="payment"
            :amount="booking.total_price"
            :empty-text="details.inquiry ? 'No online payment: trips with a Nile cruise are booked as an inquiry.' : 'No payment was recorded for this booking.'"
          />
          <references-panel :items="references" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BookingSummary from '../../components/booking/BookingSummary.vue'
import CustomerPanel from '../../components/booking/CustomerPanel.vue'
import PaymentPanel from '../../components/booking/PaymentPanel.vue'
import ReferencesPanel from '../../components/booking/ReferencesPanel.vue'
import StayStrip from '../../components/booking/StayStrip.vue'
import TravellersPanel from '../../components/booking/TravellersPanel.vue'
import bookingPage from '../../mixins/bookingPage'

// What an API without `package_details` (not deployed yet) already returns.
function fallbackDetails (b) {
  const pkg = b.package || {}
  return {
    package: { id: b.package_id, deleted: !b.package, title: pkg.packageTitle || null, image: pkg.packageImage || null },
    stay: { start: b.start_date, end: b.end_date, days: pkg.packageDuration ? Number(pkg.packageDuration) : null },
    itinerary: [],
    unsaved_days: null,
    hotels: (b.booking_hotels || []).map(row => ({
      name: row.hotel ? row.hotel.name : null,
      address: row.hotel ? row.hotel.address : null,
      start: row.start_date,
      end: row.end_date,
      code: row.jpcode
    })),
    inquiry: false
  }
}

export default {
  components: { BookingSummary, CustomerPanel, PaymentPanel, ReferencesPanel, StayStrip, TravellersPanel },
  mixins: [bookingPage],
  computed: {
    details () {
      return this.booking.package_details || fallbackDetails(this.booking)
    },
    tags () {
      return [this.details.inquiry ? 'Inquiry' : null, this.details.package.deleted ? 'Package removed' : null]
    },
    hasPickup () {
      return !!(this.contact && (this.contact.pickup_hotel_name || this.contact.pickup_room_number))
    },
    // TravelgateX hotels chosen on the website trip page (booked after the payment)
    hasTgxHotels () {
      return this.details.hotels.some(hotel => hotel.source === 'travelgatex')
    },
    references () {
      return [
        { label: 'Booking ID', value: String(this.booking.id) },
        { label: 'Package ID', value: this.details.package.id ? String(this.details.package.id) : null }
      ]
    }
  },
  methods: {
    segmentTitle (segment) {
      if (segment.type === 'cruise') return 'Nile cruise'
      return segment.city || 'City'
    },
    dayRange (from, to) {
      return from === to ? `Day ${from}` : `Days ${from}–${to}`
    },
    tgxStatusColor (status) {
      return { booked: 'success', requested: 'gold', failed_refunded: 'grey', failed_refund_failed: 'error' }[status] || 'blue-grey'
    },
    shortDate (value) {
      const d = new Date(value)
      return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
    },
    dateRange (from, to) {
      return from === to ? this.shortDate(from) : `${this.shortDate(from)} – ${this.shortDate(to)}`
    }
  }
}
</script>
